import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router';
import { ReplayEngine, type ReplayData, type ReplayState } from '../components/replay/ReplayEngine';
import ReplayCanvas from '../components/replay/ReplayCanvas';
import ReplayControls from '../components/replay/ReplayControls';
import { useReplayAudio } from '../hooks/useReplayAudio';
import { WalletAvatar } from '../components/ui/WalletAvatar';

const API_BASE = import.meta.env.VITE_API_URL || '';

const SPEEDS = [0.5, 1, 2] as const;

function formatWallet(address: string): string {
  if (address.length <= 11) return address;
  return `${address.slice(0, 4)}...${address.slice(-4)}`;
}

function formatTime(ms: number): string {
  return (ms / 1000).toFixed(3) + 's';
}

function formatPoints(score: number): string {
  return score.toLocaleString();
}

function formatPrecisionPenalty(precisionError: number): string {
  const penalty = Math.floor(precisionError * 0.8);
  return penalty > 0 ? `-${penalty}` : '0';
}

function formatDate(dateString: string): string {
  // SQLite stores UTC without 'Z' suffix, so append it for correct parsing
  const utcString = dateString.endsWith('Z') ? dateString : dateString.replace(' ', 'T') + 'Z';
  const date = new Date(utcString);
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
}

function getPhaseColor(phaseType: string): string {
  switch (phaseType) {
    case 'QUALIFIER': return '#22c55e';
    case 'SHORTLIST': return '#3b82f6';
    case 'SEMIFINAL': return '#a855f7';
    case 'FINAL': return '#fbbf24';
    default: return '#22c55e';
  }
}

function getPhaseLabel(phaseType: string): string {
  switch (phaseType) {
    case 'QUALIFIER': return 'Qualifier';
    case 'SHORTLIST': return 'Shortlist';
    case 'SEMIFINAL': return 'Semifinal';
    case 'FINAL': return 'Final';
    default: return phaseType;
  }
}

export default function Replay() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [replay, setReplay] = useState<ReplayData | null>(null);
  const [replayState, setReplayState] = useState<ReplayState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isReplayAudioMuted, setIsReplayAudioMuted] = useState(true);
  const [linkCopied, setLinkCopied] = useState(false);

  const engineRef = useRef<ReplayEngine | null>(null);

  // Initialize replay audio
  const { initializeAudio } = useReplayAudio({
    state: replayState,
    isMuted: isReplayAudioMuted,
  });

  const handleToggleMute = useCallback(async () => {
    await initializeAudio();
    setIsReplayAudioMuted((prev) => !prev);
  }, [initializeAudio]);

  // Initialize replay engine
  useEffect(() => {
    const engine = new ReplayEngine();
    engine.setOnStateChange(setReplayState);
    engineRef.current = engine;
    return () => engine.destroy();
  }, []);

  // Fetch replay data
  useEffect(() => {
    async function fetchReplay() {
      if (!sessionId) {
        setError('No session ID provided');
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_BASE}/api/replay/${sessionId}`);
        const data = await response.json();

        if (data.success && data.data) {
          setReplay(data.data);
        } else {
          setError(data.error?.message || 'Replay not found');
        }
      } catch {
        setError('Failed to load replay');
      } finally {
        setIsLoading(false);
      }
    }

    fetchReplay();
  }, [sessionId]);

  // Load replay into engine when data arrives
  useEffect(() => {
    if (replay && engineRef.current) {
      engineRef.current.load(replay);
      // Auto-play
      requestAnimationFrame(() => {
        engineRef.current?.play();
      });
    }
  }, [replay]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (!replay) return;

      switch (e.key) {
        case ' ':
          e.preventDefault();
          engineRef.current?.togglePlayPause();
          break;
        case 'r':
        case 'R':
          engineRef.current?.restart();
          engineRef.current?.play();
          break;
        case '1':
          engineRef.current?.setSpeed(SPEEDS[0]);
          break;
        case '2':
          engineRef.current?.setSpeed(SPEEDS[1]);
          break;
        case '3':
          engineRef.current?.setSpeed(SPEEDS[2]);
          break;
        case 'm':
        case 'M':
          handleToggleMute();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (replayState) {
            const newElapsed = Math.max(0, replayState.elapsed - 1000);
            engineRef.current?.seek(newElapsed);
          }
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (replayState) {
            const newElapsed = Math.min(replayState.duration, replayState.elapsed + 1000);
            engineRef.current?.seek(newElapsed);
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [replay, replayState, handleToggleMute]);

  const handlePlayPause = useCallback(() => {
    engineRef.current?.togglePlayPause();
  }, []);

  const handleSeek = useCallback((percent: number) => {
    engineRef.current?.seekPercent(percent);
  }, []);

  const handleSpeedChange = useCallback((speed: number) => {
    engineRef.current?.setSpeed(speed);
  }, []);

  const handleRestart = useCallback(() => {
    engineRef.current?.restart();
    engineRef.current?.play();
  }, []);

  const handleShareOnX = useCallback(() => {
    if (!replay) return;
    const scoreText = `${formatPoints(replay.score)} pts`;
    const timeText = formatTime(replay.survivalTimeMs);
    const isPerfect = replay.survivalTimeMs >= 45000;
    const phaseLabel = getPhaseLabel(replay.phaseType);
    const text = isPerfect
      ? `🏆 A PERFECT ${timeText} run with ${scoreText} on Hold Steady!\n\nSeason 1 ${phaseLabel}\n\nWatch this flawless performance:\nhttps://beatgames.fun/replay/${replay.sessionId}`
      : `🎯 ${scoreText} (${timeText}) on Hold Steady!\n\nSeason 1 ${phaseLabel}\n\nWatch the replay:\nhttps://beatgames.fun/replay/${replay.sessionId}`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [replay]);

  const handleCopyLink = useCallback(async () => {
    if (!replay) return;
    const url = `${window.location.origin}/replay/${replay.sessionId}`;
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  }, [replay]);

  const handleBack = useCallback(() => {
    // Check if user navigated from within the app
    // If they came directly from a shared link, referrer will be empty or external
    const referrer = document.referrer;
    const isInternalNavigation = referrer && referrer.includes(window.location.origin);

    if (isInternalNavigation) {
      navigate(-1);
    } else {
      // Navigate to archive page as a sensible default
      navigate('/finals');
    }
  }, [navigate]);

  const phaseColor = replay ? getPhaseColor(replay.phaseType) : '#22c55e';

  return (
    <div className="min-h-screen bg-[#030303] text-white overflow-x-hidden">
      {/* Grain overlay */}
      <div className="grain-overlay" />

      {/* Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: `
              linear-gradient(to right, white 1px, transparent 1px),
              linear-gradient(to bottom, white 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse 80% 50% at 50% 0%, rgba(255,255,255,0.02) 0%, transparent 60%)',
          }}
        />
      </div>

      <div className="relative z-10">
        {/* Header */}
        <header className="pt-8 pb-6 px-4">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center justify-between">
              <button
                onClick={handleBack}
                className="flex items-center gap-2 text-white/40 hover:text-white/70 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="font-mono text-xs tracking-wider">BACK</span>
              </button>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#22c55e] opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#22c55e]" />
                </span>
                <span className="text-xs font-medium text-white/60 uppercase tracking-wider">Replay</span>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <div className="max-w-5xl mx-auto px-4 pb-12">
          {isLoading ? (
            <div className="flex items-center justify-center py-32">
              <div className="text-center">
                <div className="w-12 h-12 border-2 border-white/10 border-t-white/50 rounded-full animate-spin mx-auto mb-4" />
                <p className="text-white/40 font-mono text-sm">Loading replay...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-32">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 border border-[#ef4444]/20 bg-[#ef4444]/5 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-[#ef4444]/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h2 className="font-display text-xl mb-2">Replay Not Found</h2>
                <p className="text-white/40 mb-6">{error}</p>
                <Link
                  to="/finals"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-black font-bold text-sm tracking-wider"
                >
                  <span>BROWSE ARCHIVE</span>
                </Link>
              </div>
            </div>
          ) : replay ? (
            <div className="animate-slide-up-fade">
              {/* Replay viewer */}
              <div className="border border-white/10 bg-white/[0.02] overflow-hidden">
                <div className="flex flex-col lg:flex-row">
                  {/* Canvas area */}
                  <div className="flex-1 p-4 lg:p-6">
                    <div className="relative bg-black/30 rounded-lg overflow-hidden border border-white/5">
                      <ReplayCanvas
                        state={replayState}
                        walletAddress={replay.walletAddress}
                        phaseType={replay.phaseType}
                      />
                    </div>

                    {/* Controls */}
                    <div className="mt-4">
                      <ReplayControls
                        state={replayState}
                        onPlayPause={handlePlayPause}
                        onSeek={handleSeek}
                        onSpeedChange={handleSpeedChange}
                        onRestart={handleRestart}
                        disabled={false}
                        isMuted={isReplayAudioMuted}
                        onToggleMute={handleToggleMute}
                      />
                    </div>

                    {/* Keyboard shortcuts */}
                    <div className="flex items-center justify-center gap-6 mt-4 text-white/20 text-xs">
                      <div className="flex items-center gap-1.5">
                        <kbd className="px-1.5 py-0.5 bg-white/5 border border-white/10 rounded font-mono text-[10px]">Space</kbd>
                        <span>Play/Pause</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <kbd className="px-1.5 py-0.5 bg-white/5 border border-white/10 rounded font-mono text-[10px]">R</kbd>
                        <span>Restart</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <kbd className="px-1.5 py-0.5 bg-white/5 border border-white/10 rounded font-mono text-[10px]">M</kbd>
                        <span>Mute</span>
                      </div>
                    </div>
                  </div>

                  {/* Info sidebar */}
                  <div className="w-full lg:w-72 p-4 lg:p-6 border-t lg:border-t-0 lg:border-l border-white/[0.06]">
                    <div className="font-mono text-[10px] text-white/30 tracking-wider mb-2">PLAYER</div>
                    <Link
                      to={`/profile/${replay.walletAddress}`}
                      className="flex items-center gap-3 mb-6 group"
                    >
                      <WalletAvatar address={replay.walletAddress} size={40} />
                      <span className="font-mono text-sm text-white group-hover:text-white/80 transition-colors">
                        {formatWallet(replay.walletAddress)}
                      </span>
                    </Link>

                    <div className="space-y-4 mb-6">
                      <div>
                        <div className="font-mono text-[10px] text-white/30 tracking-wider mb-1">FINAL SCORE</div>
                        <div className="font-mono text-2xl text-[#fbbf24]">{formatPoints(replay.score)} <span className="text-sm">pts</span></div>
                        <div className="font-mono text-[10px] text-white/40 mt-1">
                          {formatTime(replay.survivalTimeMs)} <span className="text-[#ef4444]/60">{formatPrecisionPenalty(replay.precisionError)}</span>
                        </div>
                      </div>
                      <div>
                        <div className="font-mono text-[10px] text-white/30 tracking-wider mb-1">PHASE</div>
                        <span
                          className="inline-block px-2 py-1 font-mono text-xs tracking-wider"
                          style={{
                            backgroundColor: `${phaseColor}20`,
                            color: phaseColor,
                          }}
                        >
                          {replay.phaseType}
                        </span>
                      </div>
                      <div>
                        <div className="font-mono text-[10px] text-white/30 tracking-wider mb-1">DATE</div>
                        <div className="font-mono text-sm text-white/60">{formatDate(replay.startedAt)}</div>
                      </div>
                    </div>

                    {replay.score >= 45000 && (
                      <div className="p-3 border border-[#fbbf24]/20 bg-[#fbbf24]/5 mb-6">
                        <div className="flex items-center gap-2">
                          <span className="text-[#fbbf24]">✨</span>
                          <span className="font-mono text-xs text-[#fbbf24]/80 tracking-wider">PERFECT RUN</span>
                        </div>
                      </div>
                    )}

                    {/* Share buttons */}
                    <div className="space-y-2">
                      <div className="font-mono text-[10px] text-white/30 tracking-wider mb-2">SHARE</div>
                      <button
                        onClick={handleShareOnX}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all font-mono text-sm tracking-wider"
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                        </svg>
                        <span>Share on X</span>
                      </button>
                      <button
                        onClick={handleCopyLink}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all font-mono text-sm tracking-wider"
                      >
                        {linkCopied ? (
                          <>
                            <svg className="w-4 h-4 text-[#22c55e]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span className="text-[#22c55e]">Copied!</span>
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                            <span>Copy Link</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="mt-8 text-center">
                <p className="font-mono text-sm text-white/30 mb-4">Think you can do better?</p>
                <Link
                  to="/play"
                  className="group inline-flex items-center gap-3 px-8 py-4 bg-white text-black font-bold tracking-wider hover:shadow-[0_0_40px_rgba(255,255,255,0.3)] transition-all"
                >
                  <span>PLAY NOW</span>
                  <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
